import { useId, useRef, type KeyboardEvent } from 'react';
import { Button } from '../../atoms/Button/Button';
import { Icon } from '../../atoms/Icon/Icon';
import { CalendarCell } from '../CalendarCell/CalendarCell';
import {
  DAYS_IN_WEEK,
  WEEKDAYS,
  buildMonthGrid,
  formatDayLabel,
  formatMonthTitle,
  isSameDay,
  isSameMonth,
  isWithin,
  startOfMonth,
  toWeeks,
  type DateRange,
} from './calendar';
import styles from './CalendarMonth.module.css';

/**
 * Figma: the `Calendar` frame inside `_Date picker menu` (123:2207 / 123:2264 /
 * 123:2465) — a `Month` header over a `Dates` grid, 8px apart.
 *
 * `Dates` is 312x312 with 16px padding: 280x280, which is seven 40px columns and
 * seven 40px rows. One row is the weekday header, so a month always draws six
 * week rows and pads with adjacent-month days.
 */

/**
 * Figma's dual-date menu gives the left calendar a "previous" arrow and the
 * right one a "next" arrow; the opposite side of each header holds a 28x28
 * Tertiary button at `opacity: 0`, purely so the space-between layout balances.
 * That is reproduced here as an inert spacer rather than an invisible control —
 * a focusable, screen-reader-visible button that does nothing is not something
 * to ship. See plan.md §7.
 *
 * Note the two real arrows are 20x20 Ghost Grey, so it is the 28px spacer — not
 * the buttons — that sets the dual header's height. The icons do not match each
 * other either: prev is `left arrow`, next is `chevron right`. Both are followed
 * as drawn, and flagged for the designer.
 */
export type CalendarMonthNavSlot = 'nav' | 'spacer';

export type CalendarMonthProps = {
  /** Any date within the month to render. */
  month: Date;
  /** Figma `Type=Selected` marks the ends, `Type=Active` everything between. */
  value?: DateRange;
  onSelectDate?: (date: Date) => void;
  onPrevMonth?: () => void;
  onNextMonth?: () => void;
  prevSlot?: CalendarMonthNavSlot;
  nextSlot?: CalendarMonthNavSlot;
  /**
   * Days the app will not accept. Out-of-month days are always disabled — that
   * is what the design draws — and this is for min/max or blackout dates on top.
   */
  isDateDisabled?: (date: Date) => boolean;
  locale?: string;
  className?: string;
};

/** How far each key moves within the 7-column grid. */
const KEY_STEP: Record<string, number> = {
  ArrowLeft: -1,
  ArrowRight: 1,
  ArrowUp: -DAYS_IN_WEEK,
  ArrowDown: DAYS_IN_WEEK,
};

export function CalendarMonth({
  month,
  value,
  onSelectDate,
  onPrevMonth,
  onNextMonth,
  prevSlot = 'nav',
  nextSlot = 'nav',
  isDateDisabled,
  locale,
  className,
}: CalendarMonthProps) {
  const titleId = useId();
  const gridRef = useRef<HTMLTableSectionElement>(null);

  const start = value?.start ?? null;
  const end = value?.end ?? null;
  const weeks = toWeeks(buildMonthGrid(month));

  /*
   * Roving tabindex — one day is reachable by Tab, the arrows move between the
   * rest. Without it, a keyboard user tabs past 42 buttons to leave the month.
   * The entry point is the selection when it is on screen, otherwise the 1st.
   * Not in Figma.
   */
  const firstOfMonth = startOfMonth(month);
  const tabbable =
    (start && isSameMonth(start, month) && start) ||
    (end && isSameMonth(end, month) && end) ||
    firstOfMonth;

  const onGridKeyDown = (event: KeyboardEvent<HTMLTableSectionElement>) => {
    const step = KEY_STEP[event.key];
    if (step === undefined || !gridRef.current) return;

    const days = Array.from(gridRef.current.querySelectorAll<HTMLButtonElement>('button'));
    const from = days.indexOf(document.activeElement as HTMLButtonElement);
    if (from < 0) return;

    /*
     * Adjacent-month days are disabled and so cannot take focus; keep stepping
     * in the same direction and stop at the edge of the month rather than
     * landing on nothing.
     */
    for (let i = from + step; i >= 0 && i < days.length; i += step) {
      if (!days[i].disabled) {
        event.preventDefault();
        days[i].focus();
        return;
      }
    }
  };

  return (
    <div className={[styles.calendar, className].filter(Boolean).join(' ')}>
      <div className={styles.month}>
        {prevSlot === 'nav' ? (
          <Button
            hierarchy="ghostGrey"
            iconLeading={<Icon name="leftArrow" />}
            onClick={onPrevMonth}
            aria-label="Previous month"
          />
        ) : (
          <span className={styles.navSpacer} aria-hidden="true" />
        )}

        <span className={styles.title} id={titleId}>
          {formatMonthTitle(month, locale)}
        </span>

        {nextSlot === 'nav' ? (
          <Button
            hierarchy="ghostGrey"
            iconLeading={<Icon name="chevronRight" />}
            onClick={onNextMonth}
            aria-label="Next month"
          />
        ) : (
          <span className={styles.navSpacer} aria-hidden="true" />
        )}
      </div>

      <div className={styles.dates}>
        <table className={styles.grid} aria-labelledby={titleId}>
          <thead>
            <tr>
              {WEEKDAYS.map((day) => (
                <th key={day.short} className={styles.headCell} scope="col" abbr={day.long}>
                  <CalendarCell interactive={false}>{day.short}</CalendarCell>
                </th>
              ))}
            </tr>
          </thead>
          {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- keyboard is handled by the composite widget (roving focus / native control); the pointer handler is a convenience. Axe checks run in Storybook and E2E. */}
          <tbody ref={gridRef} onKeyDown={onGridKeyDown}>
            {weeks.map((week) => {
              /*
               * A day is painted only if it is in range *and* in this month —
               * the design renders adjacent-month days as plain disabled cells,
               * never as part of the band. Connectors then follow from the
               * neighbours: a painted day bridges to a painted neighbour in the
               * same row, and the ends of the run get the rounded caps.
               */
              const painted = week.map(
                (day) => isSameMonth(day, month) && isWithin(day, start, end),
              );

              return (
                <tr key={week[0].toDateString()}>
                  {week.map((day, column) => {
                    const inMonth = isSameMonth(day, month);
                    const isEndpoint = isSameDay(day, start) || isSameDay(day, end);
                    const disabled = !inMonth || (isDateDisabled?.(day) ?? false);

                    return (
                      <td key={day.toDateString()} className={styles.cell}>
                        <CalendarCell
                          type={
                            inMonth && isEndpoint
                              ? 'selected'
                              : painted[column]
                                ? 'active'
                                : 'default'
                          }
                          connectLeft={painted[column] && column > 0 && painted[column - 1]}
                          connectRight={
                            painted[column] &&
                            column < DAYS_IN_WEEK - 1 &&
                            painted[column + 1]
                          }
                          disabled={disabled}
                          label={formatDayLabel(day, locale)}
                          tabIndex={isSameDay(day, tabbable) ? 0 : -1}
                          onClick={onSelectDate ? () => onSelectDate(day) : undefined}
                        >
                          {day.getDate()}
                        </CalendarCell>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
